// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#audit-log
// Prop-driven render of the audit log table. Data, filters, and pagination
// arrive as props from useAuditLog(); this component owns only the inspector
// open/close state.
// Filter state is URL-synced (deep-linkable) — the hook owns the URL params.
// Actor and app cells are click-to-filter.
import * as React from 'react';
import { ChevronRight, X } from 'lucide-react';

import { AuditEventInspector } from './AuditEventInspector';
import { auditSummary } from '@/components/greenlight/auditSummary';
import { Dash } from '@/components/greenlight/Dash';
import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { UserAvatar } from '@/components/greenlight/UserAvatar';
import { MonogramTile } from '@/components/greenlight/MonogramTile';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AuditEventSchema } from '@greenlight/contract';
import type { z } from 'zod';
import type { UseAuditLogResult, TimeRange } from './useAuditLog';
import { RANGE_LABEL } from './useAuditLog';

type AuditEvent = z.infer<typeof AuditEventSchema>;

const OUTCOME_META: Record<AuditEvent['outcome'], { variant: StatusBadgeVariant; label: string }> =
  {
    success: { variant: 'active', label: 'Success' },
    failure: { variant: 'failed', label: 'Failure' },
  };

// A cell value that, when clicked, narrows the table to that actor/app — so a
// reviewer can pivot from "what happened" to "everything this person/app did".
// Stops propagation so the click filters instead of opening the row's drawer.
function FilterLink({
  label,
  onActivate,
  className,
}: {
  label: string;
  onActivate: () => void;
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onActivate();
      }}
      aria-label={`Filter by ${label}`}
      className={
        'rounded-sm text-left underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
        (className ?? '')
      }
    >
      {label}
    </button>
  );
}

interface FilterControlsProps {
  action: string;
  appId: string;
  range: TimeRange;
  apps: { app_id: string; slug: string }[];
  onActionChange: (value: string) => void;
  onAppChange: (value: string) => void;
  onRangeChange: (value: TimeRange) => void;
}

function FilterControls({
  action,
  appId,
  range,
  apps,
  onActionChange,
  onAppChange,
  onRangeChange,
}: FilterControlsProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center">
      <Input
        type="search"
        placeholder="Filter by event (e.g. proxy, deploy, denied)…"
        aria-label="Filter by event action"
        value={action}
        onChange={(e) => onActionChange(e.target.value)}
        className="md:w-72"
      />
      <Select
        value={appId === '' ? 'all' : appId}
        onValueChange={(v) => onAppChange(v === 'all' ? '' : v)}
      >
        <SelectTrigger className="md:w-44" aria-label="Filter by app">
          <SelectValue placeholder="All apps" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All apps</SelectItem>
          {apps.map((a) => (
            <SelectItem key={a.app_id} value={a.app_id}>
              {a.slug}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={range} onValueChange={(v) => onRangeChange(v as TimeRange)}>
        <SelectTrigger className="md:w-40" aria-label="Filter by time range">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(RANGE_LABEL) as TimeRange[]).map((r) => (
            <SelectItem key={r} value={r}>
              {RANGE_LABEL[r]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export type AuditLogTableProps = UseAuditLogResult;

export function AuditLogTable({
  items,
  isLoading,
  isError,
  nextCursor,
  action,
  appId,
  actorUserId,
  range,
  cursor,
  cursorStack,
  hasFilters,
  apps,
  actorName,
  appName,
  userMap,
  setFilter,
  goNext,
  goPrev,
  clearFilters,
}: AuditLogTableProps): React.JSX.Element {
  const [selectedEvent, setSelectedEvent] = React.useState<AuditEvent | null>(null);
  const [inspectOpen, setInspectOpen] = React.useState(false);

  const openInspect = (event: AuditEvent): void => {
    setSelectedEvent(event);
    setInspectOpen(true);
  };

  const rowClassName = (e: AuditEvent): string | undefined =>
    selectedEvent?.id === e.id
      ? 'bg-primary/[0.06] shadow-[inset_3px_0_0_0_hsl(var(--primary))]'
      : undefined;

  const columns: DataListColumn<AuditEvent>[] = [
    {
      key: 'createdAt',
      header: 'Time',
      render: (e) => (
        <span className="whitespace-nowrap text-muted-foreground" title={e.createdAt}>
          {relativeTime(e.createdAt)}
        </span>
      ),
    },
    {
      key: 'summary',
      header: 'Event',
      primary: true,
      render: (e) => <span className="text-foreground">{auditSummary(e)}</span>,
    },
    {
      key: 'actor',
      header: 'Actor',
      priority: 'md',
      render: (e) => {
        const uid = e.actorUserId;
        if (uid) {
          const name = actorName(uid);
          return (
            <span className="flex items-center gap-2.5">
              <UserAvatar name={name} size={24} aria-hidden />
              <FilterLink label={name} onActivate={() => setFilter('actor', uid)} />
              {e.actorAgentSessionId && (
                <span className="inline-flex items-center h-4 px-[5px] rounded-[5px] bg-muted/80 text-muted-foreground font-mono text-[9px] uppercase tracking-[0.06em] font-semibold">
                  via agent
                </span>
              )}
            </span>
          );
        }
        if (e.actorAgentSessionId)
          return (
            <span className="flex items-center gap-2.5 text-foreground">
              <MonogramTile seed="agent" label="AG" size={24} shape="circle" muted aria-hidden />
              Builder agent
            </span>
          );
        return (
          <span className="flex items-center gap-2.5 text-muted-foreground">
            <MonogramTile seed="system" label="SY" size={24} shape="circle" muted aria-hidden />
            System
          </span>
        );
      },
    },
    {
      key: 'app',
      header: 'App',
      priority: 'md',
      render: (e) => {
        const aid = e.appId;
        if (!aid) return <Dash />;
        return (
          <FilterLink
            label={appName(aid)}
            onActivate={() => setFilter('app', aid)}
            className="font-mono text-[12px]"
          />
        );
      },
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (e) => {
        const meta = OUTCOME_META[e.outcome];
        return <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>;
      },
    },
  ];

  return (
    <div data-testid="audit-log">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Audit log</h2>
          <p className="mt-1 max-w-xl text-[12.5px] leading-relaxed text-muted-foreground">
            Every governed action, newest first. Click a row for the full record; click an actor or
            app to filter.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        <FilterControls
          action={action}
          appId={appId}
          range={range}
          apps={apps}
          onActionChange={(v) => setFilter('q', v)}
          onAppChange={(v) => setFilter('app', v)}
          onRangeChange={(v) => setFilter('range', v === 'all' ? '' : v)}
        />

        {/* Active actor filter (set by clicking an actor) — no Select for it since
            there can be many users; shown as a removable chip. */}
        {actorUserId && (
          <div className="flex items-center gap-2 text-[12px]">
            <span className="text-muted-foreground">Actor:</span>
            <button
              type="button"
              onClick={() => setFilter('actor', '')}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Clear actor filter ${actorName(actorUserId)}`}
            >
              {actorName(actorUserId)}
              <X className="size-3" aria-hidden />
            </button>
          </div>
        )}
      </div>

      <div className="mt-4">
        {isError ? (
          <Alert variant="destructive" data-testid="audit-log-error">
            <AlertTitle>Couldn't load audit events</AlertTitle>
            <AlertDescription>
              Something went wrong querying the audit log. Retry in a moment.
            </AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="space-y-2" data-testid="audit-log-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-gl border border-border p-8 text-center"
            data-testid="audit-log-empty"
          >
            <p className="text-sm text-muted-foreground">
              {hasFilters ? 'No audit events match the current filters.' : 'No audit events yet.'}
            </p>
            {hasFilters && (
              <Button variant="ghost" size="sm" className="mt-2" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Drawer opens from the focusable rowAction chevron (keyboard + AT on
                desktop and mobile). We avoid onRowActivate because it would wrap
                the mobile card in a <button>, nesting the actor/app FilterLink
                buttons inside it (invalid). */}
            <DataList
              ariaLabel="Audit events"
              items={items}
              columns={columns}
              rowKey={(e) => e.id}
              rowAction={(e) => (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="View audit event details"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    openInspect(e);
                  }}
                >
                  <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                </Button>
              )}
              rowClassName={rowClassName}
              selectedKey={selectedEvent?.id}
            />
            <div className="flex items-center justify-between gap-2 pt-3">
              <span className="text-[12px] text-muted-foreground">
                Showing {items.length} event{items.length === 1 ? '' : 's'}
              </span>
              <span className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goPrev}
                  disabled={cursorStack.length === 0 && !cursor}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goNext}
                  disabled={!nextCursor}
                  data-testid="audit-log-next"
                >
                  Next
                </Button>
              </span>
            </div>
          </>
        )}
      </div>

      <AuditEventInspector
        event={selectedEvent}
        open={inspectOpen}
        onOpenChange={(open) => {
          setInspectOpen(open);
          // Clear the selection when the drawer is dismissed so the row
          // accent and aria-current don't persist after the panel closes.
          if (!open) setSelectedEvent(null);
        }}
        actorName={selectedEvent?.actorUserId ? actorName(selectedEvent.actorUserId) : null}
        actorEmail={
          selectedEvent?.actorUserId
            ? (userMap.get(selectedEvent.actorUserId)?.email ?? null)
            : null
        }
        appLabel={selectedEvent?.appId ? appName(selectedEvent.appId) : null}
      />
    </div>
  );
}
